import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import UploadIcon from "@mui/icons-material/Upload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import TableContainer from "../../Components/TableContainer";
import TryOnStage from "./TryOnStage";
import { layerUrl as resolveLayerUrl } from "./tiwiAssets";
import tiwiBase from "../../Assets/tiwi/tiwi_base.png";
import tiwiCollar from "../../Assets/tiwi/neck_classic.png";
import roomBg from "../../Assets/tiwi/room_default.png";

// Tiwi Closet Manager (see tiwi-closet-manager-plan.md, doggo-treat-tab-plan.md).
//
// Items are cosmetic_catalog rows; art is ONE upload from the admin — the
// aligned layer PNG. The browser does the rest: validates the canvas
// contract, composites an alignment preview over the real backdrop so a
// misplaced item is caught BEFORE saving, and auto-generates the
// tight-crop thumbnail. Both PNGs then go up in one multipart request.
//
// Lifecycle mirrors Badge Manager: created HIDDEN, LIVE only with art
// (bundled launch items exempt — their art ships inside the app).

// Worn items hang off the mascot; room items decorate the Doggo's room.
// They sit on DIFFERENT canvases, because each stacks with zero
// positioning and the canvas is therefore the entire alignment contract.
const WORN_SLOTS = ["hat", "top", "neck", "costume"];
const ROOM_SLOTS = ["bed", "rug", "plant", "toy", "wall", "frame"];

const WORN_CANVAS = { w: 1024, h: 1024 };
const ROOM_CANVAS = { w: 1416, h: 1054 };

const isRoomSlot = (slot) => ROOM_SLOTS.includes(slot);
const canvasFor = (slot) => (isRoomSlot(slot) ? ROOM_CANVAS : WORN_CANVAS);

// Rarity alone decides availability — there is no separate "sell it" flag.
const RARITIES = [
  { value: "common", label: "Common — shop + dig" },
  { value: "uncommon", label: "Uncommon — shop + dig" },
  { value: "rare", label: "Rare — shop + dig" },
  { value: "ultra_rare", label: "Ultra Rare — dig only" },
  { value: "secret_rare", label: "Secret Rare — dig only, hidden until found" },
];
const SHOP_RARITIES = ["common", "uncommon", "rare"];
const isSellable = (rarity) => SHOP_RARITIES.includes(rarity);

// Resale is half the price, snapped DOWN to the nearest 5 — the one rule
// behind both a Daily Dig duplicate and a player selling from their closet
// (dig_engine.js sellValue). Mirrored here purely so pricing shows both
// numbers at once; the server is still the authority on every payout.
const SELL_RATE = 0.5;
const SELL_STEP = 5;
const sellBack = (price) =>
  Math.max(
    0,
    Math.floor(((parseInt(price, 10) || 0) * SELL_RATE) / SELL_STEP) * SELL_STEP
  );

const emptyForm = {
  id: null, code: "", name: "", slot: "hat",
  rarity: "common", price: "", sort_order: 0, covers_neck: false,
  acquisition: "standard", invite_milestone: 1,
};

// How an item is obtained. Invite exclusives skip shop AND dig — they are
// granted by the invite-reward ladder at `invite_milestone` friends.
const ACQUISITIONS = [
  { value: "standard", label: "Standard — shop / dig by rarity" },
  { value: "invite", label: "Invite exclusive — earned by inviting friends" },,
  { value: "event", label: "Event exclusive — granted at an event check-in" },
];

export default function ClosetManager() {
  const [rows, setRows] = useState([]);
  const [bundledKeys, setBundledKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create/edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Upload + alignment preview dialog
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);
  const [preview, setPreview] = useState(null); // {item, layerBlob, thumbBlob, layerUrl, thumbUrl}
  const [uploading, setUploading] = useState(false);

  // Try-on stage (fitting room) — row actions push items into it.
  const stageRef = useRef(null);

  // Optional classic-collar backdrop in the studio + alignment dialogs.
  // Defaults OFF for every slot — bare is the app's default look now
  // (2026-08-17). The checkbox remains for judging fit alongside a collar.
  const [previewCollar, setPreviewCollar] = useState(false);

  // Placement studio — any-size PNG dragged/scaled over the live composite,
  // then BAKED client-side onto the exact canvas the paper-doll contract
  // needs. {item, img, url, x, y, scale} — x/y = art centre in canvas
  // coordinates, scale = art px -> canvas px.
  const [studio, setStudio] = useState(null);
  const studioBoxRef = useRef(null);
  const dragRef = useRef(null); // {startX, startY, origX, origY} during a drag

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await AxiosInstance.get(ApiList.ADMIN_COSMETICS);
      if (res?.data?.s === 1) {
        // TableContainer's DataGrid keys on `rowid` (see its getRowId)
        setRows((res.data.r.items || []).map((it) => ({ ...it, rowid: it.id })));
        setBundledKeys(res.data.r.bundled_keys || []);
      } else {
        toast.error(res?.data?.m || "Failed to load items");
      }
    } catch (e) {
      toast.error("Failed to load items");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // ---- create / edit -----------------------------------------------------

  const openCreate = () => {
    setForm(emptyForm);
    setFormOpen(true);
  };
  const openEdit = (row) => {
    setForm({
      id: row.id, code: row.code, name: row.name, slot: row.slot,
      rarity: row.rarity || "common", price: row.price, sort_order: row.sort_order,
      covers_neck: row.covers_neck === 1,
      acquisition: row.acquisition || "standard",
      invite_milestone: row.invite_milestone || 1,
    });
    setFormOpen(true);
  };

  const saveForm = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    if (!form.id && !/^[a-z0-9_]{3,64}$/.test(form.code)) {
      return toast.error("Code must be 3-64 chars of a-z, 0-9, _ (e.g. hat_halloween)");
    }
    // Price means two things depending on the tier. On a shop tier it is
    // what the item COSTS. On a dig-only tier (Ultra/Secret) the shop never
    // lists it — the filter is by rarity — so the number is a pure
    // VALUATION that decides only what it sells back for, at 25%. 0 there
    // means "unvalued", and the item simply cannot be sold.
    const sellable = isSellable(form.rarity) && form.acquisition !== "invite" && form.acquisition !== "event";
    const price = parseInt(form.price, 10) || 0;
    if (sellable && (!Number.isInteger(price) || price < 1)) {
      return toast.error("Shop items need a price above 0");
    }

    setSaving(true);
    try {
      const url = form.id ? `${ApiList.ADMIN_COSMETICS}/${form.id}` : ApiList.ADMIN_COSMETICS;
      const coversNeck = form.covers_neck ? 1 : 0;
      const acqFields = {
        acquisition: form.acquisition,
        invite_milestone: parseInt(form.invite_milestone, 10) || 1,
      };
      const body = form.id
        ? {
            name: form.name.trim(), price, rarity: form.rarity,
            sort_order: parseInt(form.sort_order, 10) || 0,
            ...acqFields,
            ...(form.slot === "top" ? { covers_neck: coversNeck } : {}),
          }
        : {
            code: form.code.trim(), name: form.name.trim(), slot: form.slot,
            rarity: form.rarity, price, sort_order: parseInt(form.sort_order, 10) || 0,
            ...acqFields,
            ...(form.slot === "top" ? { covers_neck: coversNeck } : {}),
          };
      const res = await AxiosInstance.post(url, body);
      if (res?.data?.s === 1) {
        toast.success(form.id ? "Item updated" : "Item created (hidden) — now upload its art");
        setFormOpen(false);
        fetchItems();
      } else {
        toast.error(res?.data?.m || "Save failed");
      }
    } catch (e) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  // ---- upload: validate -> preview -> thumb -> send ----------------------

  const pickArtFor = (row) => {
    uploadTargetRef.current = row;
    setPreviewCollar(false);
    fileInputRef.current?.click();
  };

  /// Tight-crop thumbnail from a full-canvas layer <canvas>. Returns null
  /// when the layer is fully transparent.
  const makeThumb = (canvas) => {
    const LW = canvas.width, LH = canvas.height;
    const data = canvas.getContext("2d").getImageData(0, 0, LW, LH).data;
    let minX = LW, minY = LH, maxX = -1, maxY = -1;
    for (let y = 0; y < LH; y += 2) {
      for (let x = 0; x < LW; x += 2) {
        if (data[(y * LW + x) * 4 + 3] > 16) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    const pad = 8;
    const cx = Math.max(0, minX - pad);
    const cy = Math.max(0, minY - pad);
    const cw = Math.min(LW, maxX + pad) - cx;
    const ch = Math.min(LH, maxY + pad) - cy;
    const thumbCanvas = document.createElement("canvas");
    thumbCanvas.width = cw;
    thumbCanvas.height = ch;
    thumbCanvas.getContext("2d").drawImage(canvas, cx, cy, cw, ch, 0, 0, cw, ch);
    return thumbCanvas;
  };

  /// Full-canvas layer -> the alignment-check dialog (the final gate every
  /// upload passes through, template-exported and studio-baked alike).
  const stageForPreview = async (item, layerCanvasOrFile, objectUrl) => {
    let canvas = layerCanvasOrFile;
    let layerBlob;
    if (canvas instanceof HTMLCanvasElement) {
      layerBlob = await new Promise((ok) => canvas.toBlob(ok, "image/png"));
    } else {
      // Already-on-template file: draw it so the thumb crop can read pixels.
      layerBlob = layerCanvasOrFile;
      const img = new Image();
      await new Promise((ok, bad) => {
        img.onload = ok;
        img.onerror = bad;
        img.src = objectUrl;
      });
      canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
    }
    const thumbCanvas = makeThumb(canvas);
    if (!thumbCanvas) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      toast.error("The PNG is fully transparent — nothing to upload");
      return;
    }
    const thumbBlob = await new Promise((ok) => thumbCanvas.toBlob(ok, "image/png"));
    setPreview({
      item,
      layerBlob,
      thumbBlob,
      layerUrl: objectUrl || canvas.toDataURL("image/png"),
      layerUrlIsObject: !!objectUrl,
      thumbUrl: thumbCanvas.toDataURL("image/png"),
    });
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // re-picking the same file must re-fire onChange
    const item = uploadTargetRef.current;
    if (!file || !item) return;
    if (file.type !== "image/png") return toast.error("Item art must be a PNG");

    // The canvas depends on where the item lives — a room item on the
    // mascot canvas would be placed wherever that square lands in the room.
    const { w: LW, h: LH } = canvasFor(item.slot);

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      if (img.width === LW && img.height === LH) {
        // Template-exported art: already positioned, straight to the gate.
        await stageForPreview(item, file, objectUrl);
        return;
      }
      // Any other size opens the placement studio: drag and scale it over
      // the live composite, and the browser bakes it onto the canvas. This
      // is what makes uploads "stupid easy" — art no longer has to be
      // authored on the template at all.
      const fitScale = Math.min((LW * 0.5) / img.width, (LH * 0.5) / img.height);
      setStudio({
        item,
        img,
        url: objectUrl,
        x: LW / 2,
        y: LH / 2,
        scale: fitScale,
        fitScale,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      toast.error("Could not read the PNG");
    };
    img.src = objectUrl;
  };

  // ---- placement studio ---------------------------------------------------

  /// Reposition/resize EXISTING art: load the item's current layer (server
  /// upload or bundled asset), tight-crop it back out of the canvas, and
  /// open the studio with the crop sitting exactly where it sits today —
  /// so a two-pixel nudge starts from reality, not from scratch. Baking
  /// re-uploads through the normal gate; for bundled items the server copy
  /// then takes precedence in the app (TiwiArt prefers remote art), which
  /// means even launch items can be repositioned without an app release.
  const openReposition = async (row) => {
    const src = resolveLayerUrl(row);
    if (!src) return toast.error("No art to reposition — upload art first");
    setPreviewCollar(false);
    try {
      const img = new Image();
      // The API serves uploads with Access-Control-Allow-Origin: * — this
      // keeps the canvas readable (an uncredentialed cross-origin draw
      // would otherwise taint it and block the re-bake).
      img.crossOrigin = "anonymous";
      await new Promise((ok, bad) => {
        img.onload = ok;
        img.onerror = bad;
        img.src = src;
      });
      const { w: LW, h: LH } = canvasFor(row.slot);
      const full = document.createElement("canvas");
      full.width = LW;
      full.height = LH;
      const ctx = full.getContext("2d");
      // Existing layers are already full-canvas; drawing at the canvas size
      // also normalizes any legacy art with off-contract dimensions.
      ctx.drawImage(img, 0, 0, LW, LH);
      const data = ctx.getImageData(0, 0, LW, LH).data;
      let minX = LW, minY = LH, maxX = -1, maxY = -1;
      for (let y = 0; y < LH; y += 1) {
        for (let x = 0; x < LW; x += 1) {
          if (data[(y * LW + x) * 4 + 3] > 8) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return toast.error("The current art is fully transparent");
      const cw = maxX - minX + 1;
      const ch = maxY - minY + 1;
      const crop = document.createElement("canvas");
      crop.width = cw;
      crop.height = ch;
      crop.getContext("2d").drawImage(full, minX, minY, cw, ch, 0, 0, cw, ch);

      const cropImg = new Image();
      const cropUrl = crop.toDataURL("image/png");
      await new Promise((ok, bad) => {
        cropImg.onload = ok;
        cropImg.onerror = bad;
        cropImg.src = cropUrl;
      });
      // scale 1 + centre-of-bbox = pixel-identical to today's placement.
      // fitScale 1 makes the size slider read "1.0 = current size".
      setStudio({
        item: row,
        img: cropImg,
        url: cropUrl,
        x: minX + cw / 2,
        y: minY + ch / 2,
        scale: 1,
        fitScale: 1,
      });
    } catch (e) {
      toast.error("Could not load the current art for repositioning");
    }
  };

  const bakeStudio = async () => {
    if (!studio) return;
    const { item, img, x, y, scale, url } = studio;
    const { w: LW, h: LH } = canvasFor(item.slot);
    const canvas = document.createElement("canvas");
    canvas.width = LW;
    canvas.height = LH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    const dw = img.width * scale;
    const dh = img.height * scale;
    ctx.drawImage(img, x - dw / 2, y - dh / 2, dw, dh);
    setStudio(null);
    URL.revokeObjectURL(url);
    await stageForPreview(item, canvas, null);
  };

  const closeStudio = () => {
    if (studio?.url) URL.revokeObjectURL(studio.url);
    setStudio(null);
  };

  // Pointer drag in the studio box. Coordinates are converted from display
  // pixels to canvas pixels so the bake is exactly what the screen shows.
  const studioPointerDown = (e) => {
    if (!studio) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: studio.x, origY: studio.y };
  };
  const studioPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || !studio || !studioBoxRef.current) return;
    const boxW = studioBoxRef.current.clientWidth;
    const { w: LW } = canvasFor(studio.item.slot);
    const toCanvas = LW / boxW;
    setStudio((s) => s && {
      ...s,
      x: d.origX + (e.clientX - d.startX) * toCanvas,
      y: d.origY + (e.clientY - d.startY) * toCanvas,
    });
  };
  const studioPointerUp = () => {
    dragRef.current = null;
  };

  const confirmUpload = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      const fd = new FormData();
      // The layer MUST carry a .png filename: a template-path upload is the
      // picked File (has one), but a studio-baked layer is a raw
      // canvas.toBlob Blob whose FormData filename defaults to "blob" — and
      // the server's uploadFile rejects on the missing extension ("File
      // type  is not allowed", found in prod 2026-08-17). Wrap
      // unconditionally; wrapping a File is harmless.
      fd.append(
        "layer",
        new File([preview.layerBlob], "layer.png", { type: "image/png" })
      );
      fd.append("thumb", new File([preview.thumbBlob], "thumb.png", { type: "image/png" }));
      const res = await AxiosInstance.post(`${ApiList.ADMIN_COSMETICS}/${preview.item.id}/images`, fd);
      if (res?.data?.s === 1) {
        toast.success("Art uploaded");
        closePreview();
        fetchItems();
      } else {
        toast.error(res?.data?.m || "Upload failed");
      }
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const closePreview = () => {
    if (preview?.layerUrlIsObject) URL.revokeObjectURL(preview.layerUrl);
    setPreview(null);
  };

  // ---- status toggle ------------------------------------------------------

  // Permanent delete — the server refuses if anyone owns the item (hide is
  // the archive for owned items), so this is safe to offer only where
  // owner_count is 0.
  const deleteItem = async (row) => {
    if (!window.confirm(`Permanently delete '${row.name}'? This cannot be undone.`)) return;
    try {
      const res = await AxiosInstance.delete(`${ApiList.ADMIN_COSMETICS}/${row.id}`);
      if (res?.data?.s === 1) {
        toast.success(res.data.m);
        fetchItems();
      } else {
        toast.error(res?.data?.m || "Delete failed");
      }
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleStatus = async (row) => {
    const goingLive = row.status !== 1;
    if (goingLive) {
      const ok = await Swal.fire({
        title: `Make "${row.name}" LIVE?`,
        text: "It appears in every user's closet immediately.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Go live",
        confirmButtonColor: "#1d3010",
      });
      if (!ok.isConfirmed) return;
    }
    const res = await AxiosInstance.post(`${ApiList.ADMIN_COSMETICS}/${row.id}/status`, {
      status: goingLive ? 1 : 0,
    });
    if (res?.data?.s === 1) {
      toast.success(res.data.m);
      fetchItems();
    } else {
      toast.error(res?.data?.m || "Status change failed");
    }
  };

  // ---- table ---------------------------------------------------------------

  const thumbSrc = (row) =>
    row.thumb_image ? `${ApiList.IMG_URL}${row.thumb_image}` : null;

  const columns = useMemo(
    () => [
      {
        field: "thumb",
        headerName: "Art",
        width: 70,
        sortable: false,
        renderCell: (p) =>
          thumbSrc(p.row) ? (
            <img src={thumbSrc(p.row)} alt="" style={{ width: 44, height: 44, objectFit: "contain" }} />
          ) : bundledKeys.includes(p.row.asset_key) ? (
            <Tooltip title="Art ships inside the app bundle">
              <Chip label="bundled" size="small" />
            </Tooltip>
          ) : (
            <Chip label="no art" size="small" color="warning" />
          ),
      },
      { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
      { field: "code", headerName: "Code", flex: 1, minWidth: 140 },
      { field: "slot", headerName: "Slot", width: 90 },
      {
        field: "rarity",
        headerName: "Rarity",
        width: 120,
        renderCell: (p) => {
          const r = p.row.rarity || "common";
          const digOnly = !isSellable(r);
          return (
            <Chip
              label={r.replace("_", " ")}
              size="small"
              variant={digOnly ? "filled" : "outlined"}
              color={digOnly ? "secondary" : "default"}
            />
          );
        },
      },
      {
        field: "price",
        headerName: "Price",
        width: 90,
        // A dig-only tier still shows its number — it is the valuation that
        // drives resale, marked so it never reads as a shop listing.
        renderCell: (p) =>
          isSellable(p.row.rarity || "common")
            ? p.row.price
            : p.row.price > 0
            ? `${p.row.price}*`
            : "—",
      },
      {
        field: "acquisition",
        headerName: "Obtained",
        width: 110,
        renderCell: (p) =>
          p.row.acquisition === "invite"
            ? `Invite (friend ${p.row.invite_milestone ?? "?"})`
            : p.row.acquisition === "event"
              ? "Event"
              : "Shop/Dig",
      },
      {
        field: "sell_back",
        headerName: "Sells for",
        width: 95,
        sortable: false,
        renderCell: (p) => (p.row.price > 0 ? sellBack(p.row.price) : "—"),
      },
      { field: "owner_count", headerName: "Owners", width: 90 },
      {
        field: "status",
        headerName: "Status",
        width: 110,
        renderCell: (p) =>
          p.row.status === 1 ? (
            <Chip label="LIVE" size="small" sx={{ bgcolor: "#c6ff00", color: "#000", fontWeight: 700 }} />
          ) : (
            <Chip label="hidden" size="small" />
          ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 200,
        sortable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0}>
            <Tooltip title="Try on — see it on the model, combined with anything else">
              <IconButton size="small" onClick={() => stageRef.current?.tryOn(p.row)}>
                <CheckroomIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reposition / resize the current art">
              <span>
                <IconButton
                  size="small"
                  disabled={!resolveLayerUrl(p.row)}
                  onClick={() => openReposition(p.row)}
                >
                  <OpenWithIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(p.row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title="Upload art (any PNG — position it in the studio, or a pre-aligned template export)"
            >
              <IconButton size="small" onClick={() => pickArtFor(p.row)}>
                <UploadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {Number(p.row.owner_count) === 0 && (
              <Tooltip title="Delete permanently (only possible while nobody owns it)">
                <IconButton size="small" onClick={() => deleteItem(p.row)}>
                  <DeleteForeverIcon fontSize="small" sx={{ color: "#c0392b" }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={p.row.status === 1 ? "Hide" : "Go live"}>
              <IconButton size="small" onClick={() => toggleStatus(p.row)}>
                {p.row.status === 1 ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bundledKeys]
  );

  const previewIsRoom = isRoomSlot(preview?.item?.slot);

  return (
    <Box sx={{ p: 2 }}>
      <input
        type="file"
        accept="image/png"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={onFilePicked}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Closet Manager
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Doggo cosmetics and room decor. New items are hidden until art is uploaded and
            you flip them live — no app release needed. Upload ANY transparent PNG: drag
            and scale it into place in the studio, and it is baked onto the right canvas
            automatically. Template exports ({WORN_CANVAS.w}x{WORN_CANVAS.h} worn,{" "}
            {ROOM_CANVAS.w}x{ROOM_CANVAS.h} room) skip the studio.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: "#1d3010", "&:hover": { bgcolor: "#33421f" } }}
        >
          New Item
        </Button>
      </Stack>

      <TryOnStage ref={stageRef} rows={rows} />

      <Box sx={{ height: "calc(100vh - 210px)" }}>
        <TableContainer
          columns={columns}
          rows={rows}
          loading={loading}
          page={0}
          handlePageChange={() => {}}
          totalData={rows.length}
          pagination={false}
        />
      </Box>

      {/* create / edit */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{form.id ? `Edit "${form.name}"` : "New Item"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!form.id && (
              <TextField
                label="Code (permanent, e.g. hat_halloween)"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                size="small"
              />
            )}
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              size="small"
            />
            {!form.id && (
              <TextField
                select
                label="Slot (permanent)"
                value={form.slot}
                onChange={(e) => setForm({ ...form, slot: e.target.value })}
                size="small"
                helperText={`Canvas: ${canvasFor(form.slot).w}x${canvasFor(form.slot).h}`}
              >
                <ListSubheader>Worn</ListSubheader>
                {WORN_SLOTS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
                <ListSubheader>Room</ListSubheader>
                {ROOM_SLOTS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              label="Rarity"
              value={form.rarity}
              onChange={(e) => setForm({ ...form, rarity: e.target.value })}
              size="small"
              helperText="Rarity decides where an item can be obtained"
            >
              {RARITIES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="How is it obtained?"
              value={form.acquisition}
              onChange={(e) => setForm({ ...form, acquisition: e.target.value })}
              size="small"
              helperText={
                form.acquisition === "invite"
                  ? "Never in the shop or the dig — granted by the invite ladder"
                  : form.acquisition === "event"
                    ? "Never in the shop or the dig; hidden until owned — granted by an event check-in (Events page)"
                    : undefined
              }
            >
              {ACQUISITIONS.map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              ))}
            </TextField>
            {form.acquisition === "invite" && (
              <TextField
                select
                label="Invite milestone (which friend unlocks it)"
                value={form.invite_milestone}
                onChange={(e) =>
                  setForm({ ...form, invite_milestone: e.target.value })
                }
                size="small"
              >
                {[1, 2, 3, 4, 5].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m === 1 ? "1st friend" : m === 2 ? "2nd friend" : m === 3 ? "3rd friend" : `${m}th friend`}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label={
                isSellable(form.rarity)
                  ? "Price (TreatPoints)"
                  : "Value (TreatPoints)"
              }
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              size="small"
              helperText={
                isSellable(form.rarity)
                  ? `Sells back for ${sellBack(form.price)} tp (half, rounded to 5)`
                  : `Never listed in the TreatShop — this only sets what it sells back for: ${sellBack(
                      form.price
                    )} tp (half, rounded to 5). Leave 0 to make it unsellable.`
              }
            />
            <TextField
              label="Sort order"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              size="small"
            />
            {form.slot === "top" && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!form.covers_neck}
                    onChange={(e) => setForm({ ...form, covers_neck: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="body2">
                    Covers the neck — hide the collar while this top is worn
                    (puffers, high-collar coats)
                  </Typography>
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={saveForm}
            sx={{ bgcolor: "#1d3010", "&:hover": { bgcolor: "#33421f" } }}
          >
            {form.id ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* placement studio — drag/scale any PNG onto the canvas */}
      <Dialog open={!!studio} onClose={closeStudio} maxWidth="md" fullWidth>
        <DialogTitle>Place the art — {studio?.item?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Drag to move, slider (or mouse wheel) to resize. What you see over the{" "}
            {isRoomSlot(studio?.item?.slot) ? "room" : "Doggo"} is exactly what ships.
          </Typography>
          {studio && (() => {
            const { w: LW, h: LH } = canvasFor(studio.item.slot);
            const boxW = 480;
            const boxH = Math.round((boxW * LH) / LW);
            const disp = boxW / LW;
            const dw = studio.img.width * studio.scale * disp;
            const dh = studio.img.height * studio.scale * disp;
            const room = isRoomSlot(studio.item.slot);
            return (
              <Stack spacing={1} alignItems="center">
                <Box
                  ref={studioBoxRef}
                  onPointerDown={studioPointerDown}
                  onPointerMove={studioPointerMove}
                  onPointerUp={studioPointerUp}
                  onWheel={(e) => {
                    const factor = e.deltaY < 0 ? 1.06 : 1 / 1.06;
                    setStudio((s) => s && {
                      ...s,
                      scale: Math.min(s.fitScale * 6, Math.max(s.fitScale * 0.1, s.scale * factor)),
                    });
                  }}
                  sx={{
                    position: "relative",
                    width: boxW,
                    height: boxH,
                    bgcolor: "#eaf2d8",
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: dragRef.current ? "grabbing" : "grab",
                    touchAction: "none",
                    userSelect: "none",
                  }}
                >
                  {room ? (
                    <img src={roomBg} alt="" draggable={false}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />
                  ) : (
                    <>
                      <img src={tiwiBase} alt="" draggable={false}
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />
                      {previewCollar && (
                        <img src={tiwiCollar} alt="" draggable={false}
                          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />
                      )}
                    </>
                  )}
                  <img
                    src={studio.url}
                    alt=""
                    draggable={false}
                    style={{
                      position: "absolute",
                      left: studio.x * disp - dw / 2,
                      top: studio.y * disp - dh / 2,
                      width: dw,
                      height: dh,
                      pointerEvents: "none",
                    }}
                  />
                  {/* Centre-point crosshair — the (X, Y) the fields show. */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: studio.x * disp - 9,
                      top: studio.y * disp - 9,
                      width: 18,
                      height: 18,
                      pointerEvents: "none",
                      "&::before, &::after": {
                        content: '""',
                        position: "absolute",
                        bgcolor: "rgba(29,48,16,0.85)",
                        boxShadow: "0 0 2px rgba(255,255,255,0.9)",
                      },
                      "&::before": { left: 8, top: 0, width: "2px", height: 18 },
                      "&::after": { left: 0, top: 8, width: 18, height: "2px" },
                    }}
                  />
                </Box>
                <Box sx={{ width: boxW, px: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Size
                  </Typography>
                  <Slider
                    size="small"
                    min={0.1}
                    max={6}
                    step={0.02}
                    value={studio.scale / studio.fitScale}
                    onChange={(_, v) =>
                      setStudio((s) => s && { ...s, scale: s.fitScale * v })
                    }
                  />
                  {/* Exact placement readout + manual input, so a recolor
                      can be baked at PIXEL-IDENTICAL position and scale:
                      note X / Y / Scale from the first item, type them in
                      for each variant. Values are absolute canvas units —
                      independent of this dialog's zoom. */}
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mt: 1 }}
                  >
                    <TextField
                      label="X (centre px)"
                      size="small"
                      type="number"
                      sx={{ width: 120 }}
                      value={Math.round(studio.x)}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (Number.isFinite(v))
                          setStudio((s) => s && { ...s, x: v });
                      }}
                    />
                    <TextField
                      label="Y (centre px)"
                      size="small"
                      type="number"
                      sx={{ width: 120 }}
                      value={Math.round(studio.y)}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (Number.isFinite(v))
                          setStudio((s) => s && { ...s, y: v });
                      }}
                    />
                    <TextField
                      label="Scale"
                      size="small"
                      type="number"
                      inputProps={{ step: 0.001 }}
                      sx={{ width: 110 }}
                      value={Number(studio.scale.toFixed(4))}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (Number.isFinite(v) && v > 0)
                          setStudio((s) => s && { ...s, scale: v });
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      renders{" "}
                      {Math.round(studio.img.width * studio.scale)}×
                      {Math.round(studio.img.height * studio.scale)} px
                      <br />
                      source {studio.img.width}×{studio.img.height}
                    </Typography>
                  </Stack>
                  {!room && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={previewCollar}
                          onChange={(e) => setPreviewCollar(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Show classic collar (backdrop only — not baked in)
                        </Typography>
                      }
                    />
                  )}
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStudio}>Cancel</Button>
          <Button
            variant="contained"
            onClick={bakeStudio}
            sx={{ bgcolor: "#1d3010", "&:hover": { bgcolor: "#33421f" } }}
          >
            Bake &amp; continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* alignment preview before upload */}
      <Dialog open={!!preview} onClose={closePreview} maxWidth="sm" fullWidth>
        <DialogTitle>Check alignment — {preview?.item?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {previewIsRoom
              ? "This is exactly where the item sits in the room in the app. If it looks off, fix the art on the template and re-upload — do not save misaligned art."
              : "This is exactly how the item sits on Tiwi in the app. If it looks off, fix the art on the template and re-upload — do not save misaligned art."}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                position: "relative",
                width: 320,
                height: previewIsRoom
                  ? Math.round((320 * ROOM_CANVAS.h) / ROOM_CANVAS.w)
                  : 320,
                bgcolor: "#eaf2d8",
                borderRadius: 2,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {/* Room items preview over the room; worn items over the
                  mascot. Previewing a rug on the dog would tell you
                  nothing about where it actually lands. */}
              {previewIsRoom ? (
                <img src={roomBg} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
              ) : (
                <>
                  <img src={tiwiBase} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
                  {previewCollar && (
                    <img src={tiwiCollar} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
                  )}
                </>
              )}
              {preview && (
                <img
                  src={preview.layerUrl}
                  alt=""
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                />
              )}
            </Box>
            <Box>
              {!previewIsRoom && (
                <FormControlLabel
                  sx={{ display: "block", mb: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={previewCollar}
                      onChange={(e) => setPreviewCollar(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Show classic collar</Typography>}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                Auto-generated thumbnail
              </Typography>
              <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1, mt: 0.5 }}>
                {preview && (
                  <img src={preview.thumbUrl} alt="" style={{ width: 96, height: 96, objectFit: "contain" }} />
                )}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Cancel</Button>
          <Button
            variant="contained"
            disabled={uploading}
            onClick={confirmUpload}
            sx={{ bgcolor: "#1d3010", "&:hover": { bgcolor: "#33421f" } }}
          >
            Looks right — upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
